import dayjs from "dayjs";
import { useQuery, UseQueryOptions } from "react-query";

import { BaseResponse2 } from "src/services/service.type";
import { CoreQrReportItem } from "src/types";
import { api } from "src/utils";

export interface UsePartnerReportFilters {
  searchCustomer?: string;
  channels?: string[];
  appName?: string;
  startTime?: Date;
  endTime?: Date;
}

interface PartnerReportResponseData {
  total: number;
  results: CoreQrReportItem[];
}

const NO_TIMEZONE_FORMAT = "YYYY-MM-DD HH:mm:ss";

export function usePartnerReport<T = PartnerReportResponseData>(
  filters?: UsePartnerReportFilters,
  options?: UseQueryOptions<PartnerReportResponseData, unknown, T, any[]>,
) {
  return useQuery(
    ["partnerReport", filters],
    async (): Promise<PartnerReportResponseData> => {
      const { channels, startTime, endTime, ...restFilters } = filters || {};
      const params = {
        ...restFilters,
        startTime: startTime ? dayjs(startTime).utc().format(NO_TIMEZONE_FORMAT) : undefined,
        endTime: endTime ? dayjs(endTime).utc().format(NO_TIMEZONE_FORMAT) : undefined,
      };
      // The core report takes one channel per call, so partner channels are
      // fetched separately and merged back into a single newest-first list.
      const requestedChannels = channels && channels.length > 0 ? channels : [undefined];
      const responses = await Promise.all(
        requestedChannels.map((channel) =>
          api.get<BaseResponse2<PartnerReportResponseData>>("report/core-qr", {
            params: { ...params, channel },
          }),
        ),
      );
      const pages = responses.map((response) => response.data.data);
      return {
        total: pages.reduce((sum, page) => sum + (page?.total || 0), 0),
        results: pages
          .reduce(
            (merged: CoreQrReportItem[], page) => merged.concat(page?.results || []),
            [],
          )
          .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()),
      };
    },
    options,
  );
}
